import { useCallback, useState } from "react";
import { useSettingsApiClient } from "@api/use-settings-api-client";
import { FaroButton } from "@components/common/faro-button";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectProps } from "@custom-types/project-types";
import { useOnUploadFiles } from "@hooks/data-management/use-on-upload-any-files";
import { UploadSettings } from "@hooks/data-management/use-upload-multiple-scans";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui/material";
import {
  AnyFilesInfo,
  ExistingFileTypes,
  WorkflowType,
  SettingsResponsePC,
  SettingsResponseReflection,
  SettingsResponseWorkflowStopAfter,
} from "@pages/data-management/data-management-types";
import { getTotalFileSizeOfFileInfos, setPublishSettings } from "@pages/data-management/data-management-utils";
import { sphereColors } from "@styles/common-colors";

interface Props extends BaseProjectProps {
  /** Type of the workflow, automatic or manual. */
  workflowType: WorkflowType,
  /** Flags for the occurring file types, incl. some combined meta information like hasLsDataV2. */
  existingFileTypes: ExistingFileTypes,
  /** Settings object received by getPublishSettings. */
  settingPC: SettingsResponsePC,
  /** Settings object received by getPublishSettings. */
  settingReflection: SettingsResponseReflection,
  /** Settings object received by getPublishSettings. */
  settingWorkflowStopAfter: SettingsResponseWorkflowStopAfter,
  /** File infos constructed by useOnCheckFiles. */
  fileInfos: AnyFilesInfo[],
  /** Callback for setting the file infos. */
  setFileInfos: React.Dispatch<React.SetStateAction<AnyFilesInfo[]>>,
  /** Callback for setting the total file size, which is also shown in the upload page of the wizard. */
  setTotalFileSize: React.Dispatch<React.SetStateAction<number>>,
  /** Flag whether file info is currently getting collected and the dropzone should show a spinner. */
  isDropzoneLoading: boolean,
}

/**
 * Component with Import and Cancel buttons.
 * The Import button applies the settings and then starts the upload of the files.
 * The Cancel button clears the file infos and returns to the file selection step.
 */
export function ImportButtonsComponent({
  project,
  workflowType,
  existingFileTypes,
  settingPC,
  settingReflection,
  settingWorkflowStopAfter,
  fileInfos,
  setFileInfos,
  setTotalFileSize,
  isDropzoneLoading,
}: Props): JSX.Element {
  const { isScreenSmOrSmaller, isScreenXlOrLarger } = useMediaQueryList();
  const settingsApiClient = useSettingsApiClient();
  const onUploadFiles = useOnUploadFiles(project);
  const { handleErrorWithToast } = useErrorContext();
  const [isLoadingSettings, setIsLoadingSettings] = useState<boolean>(false);
  const isLoading = isDropzoneLoading || isLoadingSettings;

  /**
   * Save the settings with a "last writer wins" approach, as specified by PM.
   * Shows an error toast if saving fails.
   * @returns True on success, false on error.
   */
  const applySettings = useCallback(() => {
    return setPublishSettings(project.id, settingsApiClient, handleErrorWithToast,
      { settingPC, settingReflection, settingWorkflowStopAfter }
    );
  }, [handleErrorWithToast, project.id, settingPC, settingReflection, settingWorkflowStopAfter, settingsApiClient]);

  /** Handles a click on the Import button to start the upload. */
  async function startUpload(): Promise<void> {
    setIsLoadingSettings(true);

    if (existingFileTypes.hasLsDataV2 && !await applySettings()) {
      setIsLoadingSettings(false);
      return;
    }

    try {
      const uploadSettings: UploadSettings = {
        workflowType,
        shouldGeneratePointCloud: settingPC.value,
        shouldApplyReflectionFilters: settingReflection.value,
      };

      const totalSize = getTotalFileSizeOfFileInfos(fileInfos);
      setTotalFileSize(totalSize);
      await onUploadFiles(fileInfos, uploadSettings);
    } finally {
      setFileInfos([]);
      setIsLoadingSettings(false);
    }
  }

  let marginBottom;
  if (isScreenSmOrSmaller) {
    marginBottom = undefined;
  } else if (isScreenXlOrLarger) {
    marginBottom = "60px";
  } else {
    marginBottom = "30px";
  }

  return (
    <Box sx={{
      position: isScreenSmOrSmaller ? "fixed" : undefined,
      bottom: isScreenSmOrSmaller ? "0px" : undefined,
      left: isScreenSmOrSmaller ? "0px" : undefined,
      width: "100%",
      mt: "auto",
      mb: marginBottom,
      padding: isScreenSmOrSmaller ? "12px" : undefined,
      pt: isScreenSmOrSmaller ? undefined : "40px",
      textAlign: "right",
      backgroundColor: isScreenSmOrSmaller ? sphereColors.pureWhite : undefined,
      borderTop: isScreenSmOrSmaller ? `1px solid ${sphereColors.gray200}` : undefined,
    }}>
      <Stack direction="row" sx={{ display: "inline-flex" }}>
        <FaroTextButton
          isDisabled={isLoading}
          sx={{
            pl: "30px",
            pr: "30px",
            fontSize: "16px",
            fontWeight: "600",
          }}
          onClick={() => setFileInfos([])}
        >
          Cancel
        </FaroTextButton>

        <FaroButton
          isLoading={isLoading}
          isDisabled={isLoading || existingFileTypes.hasInvalid}
          sx={{
            ml: "10px",
            pl: "30px",
            pr: "30px",
            fontSize: "16px",
            fontWeight: "600",
          }}
          onClick={() => void startUpload()}
        >
          Import
        </FaroButton>
      </Stack>
    </Box>
  );
}
